import { useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { createAdminMcpOAuthAuthorizationCode } from "../api";
import logibudLogo from "../assets/logo/logibud_primary_darkblue.svg";
import { useUserSession } from "../context/UserContext";

const requiredParams = [
  "client_id",
  "redirect_uri",
  "response_type",
  "code_challenge",
  "code_challenge_method",
];

const AdminOAuthAuthorizePage = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const { userSession, authReady } = useUserSession();
  const [isApproving, setIsApproving] = useState(false);
  const [allowOnboarding, setAllowOnboarding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const missing = useMemo(
    () => requiredParams.filter((name) => !params.get(name)),
    [params],
  );
  const invalid =
    missing.length > 0 ||
    params.get("response_type") !== "code" ||
    params.get("code_challenge_method") !== "S256";
  const isAdmin = userSession?.user.isAdmin === true;
  const canApprove =
    authReady &&
    isAdmin &&
    !invalid &&
    !isApproving &&
    !!userSession?.token.accessToken;
  const continuePath = `${location.pathname}${location.search}`;

  const approve = async () => {
    if (!canApprove || !userSession) return;
    setIsApproving(true);
    setError(null);
    try {
      const response = await createAdminMcpOAuthAuthorizationCode(
        userSession.token.accessToken,
        {
          client_id: params.get("client_id") || "",
          redirect_uri: params.get("redirect_uri") || "",
          response_type: "code",
          state: params.get("state"),
          code_challenge: params.get("code_challenge") || "",
          code_challenge_method: "S256",
          scope: allowOnboarding ? "admin:read admin:provision" : "admin:read",
          resource: params.get("resource"),
          name: "Claude Admin MCP",
        },
      );
      if (!response?.redirect_uri)
        throw new Error("Missing OAuth redirect URL");
      window.location.assign(response.redirect_uri);
    } catch (caught: any) {
      setError(caught?.message || "Could not authorize Admin MCP.");
      setIsApproving(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-12 text-gray-900">
      <section className="mx-auto w-full max-w-lg overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <header className="border-b border-gray-200 px-6 py-6">
          <img alt="Logibud" className="mb-5 h-9 w-auto" src={logibudLogo} />
          <h1 className="text-2xl font-semibold">
            Authorize Logibud Admin MCP
          </h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            Give Claude temporary access to read across Logibud workspaces.
          </p>
        </header>
        <div className="space-y-5 px-6 py-6">
          {!authReady ? (
            <p>Checking login status...</p>
          ) : !userSession ? (
            <p>
              <Link
                className="font-medium text-lgb-primary hover:underline"
                to={`/login?continue=${encodeURIComponent(continuePath)}`}
              >
                Log in to Logibud
              </Link>
            </p>
          ) : !isAdmin ? (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              Your current Logibud account is not an administrator.
            </p>
          ) : invalid ? (
            <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              Invalid OAuth request
              {missing.length ? `; missing ${missing.join(", ")}` : ""}.
            </p>
          ) : (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
              <p className="font-medium">Read-only, cross-workspace access</p>
              <label className="mt-3 flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={allowOnboarding}
                  onChange={(event) => setAllowOnboarding(event.target.checked)}
                />
                <span>
                  Also allow creating new workspaces and onboarding resources
                  after confirmation. Existing customer data cannot be edited or
                  deleted.
                </span>
              </label>
              <p className="mt-2">
                Access expires after one hour. Logibud rechecks your admin role
                on every request.
              </p>
            </div>
          )}
          {error && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-900">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Link
              className="rounded-md border border-gray-300 px-5 py-2.5 text-sm"
              to="/"
            >
              Cancel
            </Link>
            <button
              className="rounded-md bg-lgb-primary px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!canApprove}
              onClick={approve}
              type="button"
            >
              {isApproving ? "Authorizing..." : "Authorize Claude"}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AdminOAuthAuthorizePage;
